import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { MailCheck } from "lucide-react";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SEC = 60;

const VerifyEmail = () => {
  const { t } = useLang();
  const { finishVerification, login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = (params.get("email") || "").toLowerCase().trim();
  const notice = params.get("notice");

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!email) navigate("/register", { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    setCooldown(RESEND_COOLDOWN_SEC);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const setDigit = (idx, value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = cleaned;
      return next;
    });
    if (cleaned && idx < CODE_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < CODE_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData || window.clipboardData).getData("text");
    const cleaned = pasted.replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!cleaned) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill("");
    for (let i = 0; i < cleaned.length; i++) next[i] = cleaned[i];
    setDigits(next);
    const focusIdx = Math.min(cleaned.length, CODE_LENGTH - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    const code = digits.join("");
    if (code.length !== CODE_LENGTH) {
      toast.error(t?.verify?.error_incomplete || "Inserisci tutte le 6 cifre del codice");
      return;
    }
    setSubmitting(true);
    try {
      if (typeof finishVerification === "function") {
        await finishVerification(email, code);
      } else {
        const res = await api.post("/auth/verify-email", { email, code });
        if (res.data?.access_token) {
          localStorage.setItem("genio_token", res.data.access_token);
          if (typeof authLogin === "function") {
            authLogin(res.data);
          }
        }
      }
      toast.success(t?.verify?.success || "Email verificata con successo!");
      navigate("/dashboard");
    } catch (err) {
      let errorMessage = "Codice non valido o scaduto";
      try {
        errorMessage = formatApiError ? formatApiError(err) : (err?.response?.data?.detail || err?.message || errorMessage);
      } catch (errParsing) {
        errorMessage = "Si è verificato un errore durante la verifica";
      }
      toast.error(errorMessage);
      setDigits(Array(CODE_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const requestResend = async () => {
    if (resending || cooldown > 0) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success(t?.verify?.resent || "Nuovo codice inviato via email!");
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      let errorMessage = "Errore durante l'invio del codice";
      try {
        errorMessage = formatApiError ? formatApiError(err) : (err?.response?.data?.detail || err?.message || errorMessage);
      } catch (e) {
        // Ignora
      }
      toast.error(errorMessage);
    } finally {
      setResending(false);
    }
  };

  return (
    <main
      className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-20"
      data-testid="verify-email-page"
    >
      <div className="max-w-md mx-auto">
        <span className="overline">{t?.verify?.overline || "VERIFICA EMAIL"}</span>
        <div className="mt-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-[#F1D26B] to-[#7A5A18] border border-[#F1D26B]/60 shrink-0">
            <MailCheck className="w-5 h-5 text-[#0B1437]" strokeWidth={1.8} />
          </div>
          <h1 className="font-heading text-4xl tracking-tighter text-[#EDE7D8]">
            {t?.verify?.title || "Un ultimo passo"}
          </h1>
        </div>
        <p className="mt-4 text-[#EDE7D8]/80 leading-relaxed">
          {t?.verify?.desc_prefix || "Abbiamo inviato un codice a 6 cifre a"}{" "}
          <span className="text-[#D4AF37] font-semibold">{email}</span>.{" "}
          {t?.verify?.desc_suffix || "Inseriscilo qui sotto per attivare il tuo account."}
        </p>
        {notice === "verification_required" && (
          <p className="mt-3 text-sm text-[#F1D26B]" data-testid="verify-notice">
            {t?.verify?.notice_required || "Conferma l'email per continuare"}
          </p>
        )}

        <form onSubmit={submit} className="mt-8 grid-card p-8" data-testid="verify-form">
          <label className="field-label">{t?.verify?.code_label || "CODICE DI VERIFICA"}</label>
          <div className="mt-3 flex gap-2 justify-between" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="verify-digit"
                data-testid={`verify-digit-${i}`}
                autoFocus={i === 0}
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-pill w-full justify-center mt-8 cursor-pointer"
            data-testid="verify-submit"
          >
            {submitting ? (t?.verify?.verifying || "Verifica in corso...") : (t?.verify?.submit || "Verifica")}
          </button>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={requestResend}
              disabled={resending || cooldown > 0}
              className="text-sm text-[#D4AF37] hover:text-[#F1D26B] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              data-testid="verify-resend"
            >
              {cooldown > 0
                ? `${t?.verify?.resend_wait || "Rimanda tra"} ${cooldown}s`
                : resending
                ? (t?.verify?.resending || "Invio in corso...")
                : (t?.verify?.resend || "Non hai ricevuto il codice? Rimandalo")}
            </button>
          </div>
        </form>

        <Link
          to="/login"
          className="mt-6 inline-block text-sm text-[#D4AF37] hover:text-[#F1D26B] transition-colors duration-300"
          data-testid="verify-back-to-login"
        >
          {t?.verify?.back_to_login || "← Torna al login"}
        </Link>
      </div>
    </main>
  );
};

export default VerifyEmail;
